import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Box,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import { getLatestStocks } from "../api/latest-stocks";
import { formatDate } from "../utils/deteUtils";
import type { LatestStockItem } from "../types/disaster";

const StyledCircularBackground = styled(CircularProgress)`
  color: #bfbfbf;
`;

const StyledCircularBar = styled(CircularProgress)`
  position: absolute;
`;

const typeVaridate = (type: number): string =>
  type === 1
    ? "orange"
    : type === 2
      ? "red"
      : type === 3
        ? "green"
        : type === 4
          ? "blue"
          : "black";

type MaterListProps = {
  siteId?: number;
  flexType?: string;
};

export default function MaterList({ siteId }: MaterListProps) {
  const [latestStocks, setLatestStocks] = useState<LatestStockItem[]>([]);
  const [latestStockState, setLatestStockState] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const getStocksData = async () => {
      setLatestStocks([]);

      if (siteId === undefined) {
        setLatestStockState("SSが選択されていません");
        return;
      }

      setLatestStockState(null);

      try {
        const response = await getLatestStocks({
          site_id: [siteId],
          order: null,
          sort: null,
          page: 1,
          limit: null,
        });
        if (cancelled) {
          return;
        }
        if (response.status !== "ok") {
          setLatestStocks([]);
          setLatestStockState("表示権限がありません");
          return;
        }
        const data = response.latest_stocks ?? [];
        setLatestStocks(data);
        setLatestStockState(
          data.length === 0 ? "POSデータが連携されていません" : null
        );
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("API呼び出しエラー", error);
        setLatestStocks([]);
        setLatestStockState("表示権限がありません");
      }
    };

    getStocksData();

    return () => {
      cancelled = true;
    };
  }, [siteId]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
        }}
      >
        {latestStockState != null ? (
          <Box sx={{ fontWeight: 700 }}>{latestStockState}</Box>
        ) : null}

        {latestStocks.map((latestStock) => {
          return (
            <Box key={latestStock.id}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "32px",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Box sx={{ fontSize: "32px", fontWeight: "bold" }}>
                    {latestStock.stock}
                  </Box>
                  <Box>
                    {latestStock.tank.oil_type === 1
                      ? "ハイオク"
                      : latestStock.tank.oil_type === 2
                        ? "レギュラー"
                        : latestStock.tank.oil_type === 3
                          ? "軽油"
                          : latestStock.tank.oil_type === 4
                            ? "灯油"
                            : "その他"}
                  </Box>
                </Box>
                <Box position="relative" display="inline-flex">
                  <StyledCircularBackground
                    variant="determinate"
                    size={100}
                    value={100}
                    sx={{
                      color: "outline.light",
                      ".MuiCircularProgress-circle": {
                        strokeWidth: "4",
                        r: 20,
                      },
                    }}
                  />
                  <StyledCircularBar
                    variant="determinate"
                    size={100}
                    value={
                      (latestStock.stock / latestStock.tank.volume) * 100
                    }
                    sx={{
                      color: typeVaridate(latestStock.tank.oil_type),
                      ".MuiCircularProgress-circle": {
                        strokeWidth: "4",
                        r: 20,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Table sx={{ writingMode: "horizontal-tb" }}>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      タンクNo
                    </TableCell>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "regular",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      {latestStock.tank.no}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      容量
                    </TableCell>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "regular",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      {latestStock.tank.volume}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      出荷
                    </TableCell>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "regular",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      {latestStock.sale}L
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      在庫日時
                    </TableCell>
                    <TableCell
                      sx={{
                        writingMode: "horizontal-tb",
                        color: "outline.dark",
                        fontWeight: "regular",
                        borderBottom: "2px solid outline.light",
                        padding: "8px",
                        width: "50%",
                      }}
                    >
                      {formatDate(latestStock.update_at)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })}
      </Box>
    </>
  );
}
